/**
 * Rückgängig und Wiederholen für Umbauten (seit 27.10.2026).
 *
 * Jeder Umbau ist ein eigener Commit auf staging: Abschnitt verschoben, Seite
 * angelegt, Menü geändert. Dieser Speicher merkt sich die Hashes der eigenen
 * Umbauten dieser Sitzung; Cmd+Z schickt den jüngsten an den Server, der ihn
 * mit `git revert` zurücknimmt (UmbauRueckgaengig). Wiederholen nimmt den
 * Rückgängig-Commit zurück. Die Liste gilt für diese Sitzung im Browser; was
 * älter ist oder von anderen stammt, holt der Verlauf (HistoryDrawer) zurück.
 *
 * Texte sind hier nicht dabei: In einem Eingabefeld gehört Cmd+Z dem Feld,
 * und gespeicherte Texte haben ihren eigenen Verlauf je Feld.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api } from '@/lib/api'
import { abschnittsfehler } from '@/lib/abschnittsfehler'
import { uebersetze } from '@/lib/uebersetzen'
import { useToast } from '@/composables/useToast'

export type UmbauArt =
  | 'anlegen' | 'verschieben' | 'duplizieren' | 'loeschen' | 'variante' | 'hintergrund' | 'abstand' | 'schleier' | 'geraet' | 'bewegung' | 'anker' | 'ausblenden'
  | 'eintrag_anlegen' | 'eintrag_verschieben' | 'eintrag_loeschen'
  | 'sichtbar' | 'entwurf' | 'suche_aus' | 'suche_an' | 'landing_an' | 'landing_aus' | 'beitrag_an' | 'beitrag_aus' | 'seite_anlegen' | 'seite_loeschen' | 'adresse' | 'menue'

interface Schritt {
  projekt: string
  hash: string
  art: UmbauArt
}

/** Mehr hält auch der Server nicht vor (UmbauRueckgaengig::HOECHSTENS). */
const HOECHSTENS = 50

export const useUmbauVerlaufStore = defineStore('umbauVerlauf', () => {
  const toast = useToast()
  const zurueck = ref<Schritt[]>([])
  const vor = ref<Schritt[]>([])
  const arbeitet = ref(false)
  /** Zählt hoch nach jedem Rückgängig oder Wiederholen: Die Editor-Ansicht
   *  lädt daraufhin Seiten, Texte und Vorschau nach. */
  const stand = ref(0)
  /** Sekunden, als der letzte Schritt abging: Der Bau danach zählt. */
  const seit = ref(0)

  /** Einen gelungenen Umbau merken. Ohne Commit gibt es nichts zurückzunehmen. */
  function merke(projekt: string, hash: string | null | undefined, art: UmbauArt): void {
    if (!hash) return
    zurueck.value = [...zurueck.value.filter((s) => s.projekt === projekt), { projekt, hash, art }].slice(-HOECHSTENS)
    vor.value = []
  }

  function letzter(liste: Schritt[], projekt: string): Schritt | undefined {
    return [...liste].reverse().find((s) => s.projekt === projekt)
  }

  /** Was Cmd+Z (`zurueck`) bzw. Wiederholen (`vor`) als Nächstes träfe. */
  function naechste(richtung: 'zurueck' | 'vor', projekt: string): UmbauArt | null {
    return letzter(richtung === 'zurueck' ? zurueck.value : vor.value, projekt)?.art ?? null
  }

  /** Den jüngsten eigenen Umbau zurücknehmen. */
  async function rueckgaengig(projekt: string): Promise<void> {
    await schritt(projekt, zurueck, vor, 'rueckgaengig.erledigt')
  }

  /** Den zuletzt zurückgenommenen Umbau wiederholen. */
  async function wiederholen(projekt: string): Promise<void> {
    await schritt(projekt, vor, zurueck, 'rueckgaengig.wiederholt')
  }

  async function schritt(
    projekt: string,
    von: typeof zurueck,
    nach: typeof zurueck,
    meldung: string,
  ): Promise<void> {
    const s = letzter(von.value, projekt)
    if (!s || arbeitet.value) return
    arbeitet.value = true
    seit.value = Math.floor(Date.now() / 1000)
    von.value = von.value.filter((x) => x !== s)
    try {
      const antwort = await api.rueckgaengig(projekt, s.hash)
      if (antwort.git.hash) nach.value = [...nach.value, { projekt, hash: antwort.git.hash, art: s.art }]
      toast.info(uebersetze(meldung, { was: uebersetze(`rueckgaengig.art.${s.art}`) }))
      stand.value++
    } catch (e) {
      // Was der Server nicht zurücknimmt, bleibt auch hier weg: Ein zweiter
      // Versuch scheiterte genauso.
      toast.error(uebersetze('rueckgaengig.ging_nicht'), abschnittsfehler(e))
    } finally {
      arbeitet.value = false
    }
  }

  function $reset(): void {
    zurueck.value = []
    vor.value = []
    arbeitet.value = false
  }

  return { zurueck, vor, arbeitet, stand, seit, merke, naechste, rueckgaengig, wiederholen, $reset }
})
